import { parseLocalDate } from './dateHelpers';

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

export function formatDate(date: Date | string): string {
  // If string in YYYY-MM-DD format, parse as local date to avoid timezone issues
  const d = typeof date === 'string'
    ? (date.includes('T') ? new Date(date) : parseLocalDate(date))
    : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return formatDate(d);
}

export function formatPercentage(current: number, target: number): number {
  if (target === 0) return 0;
  return Math.min(Math.round((current / target) * 100), 100);
}

export function formatSteps(steps: number): string {
  if (steps >= 1000) {
    return `${(steps / 1000).toFixed(1)}k`;
  }
  return steps.toString();
}

export function formatShortDate(date: Date | string): string {
  // If string in YYYY-MM-DD format, parse as local date to avoid timezone issues
  const d = typeof date === 'string'
    ? (date.includes('T') ? new Date(date) : parseLocalDate(date))
    : date;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/**
 * Get time-based greeting
 */
export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
}

/**
 * Format duration in seconds to human-readable string
 * @param totalSeconds - Duration in seconds
 * @returns Formatted string like "4h 30m" or "5h 0m 30s"
 */
export function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0 && seconds > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Format seconds to "H:MM:SS" clock display (e.g., "3:42:15")
 * Used for marathon finish times
 */
export function formatDurationClock(seconds: number): string {
  if (!seconds || seconds <= 0) return "0:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

/**
 * Format seconds to "Xh Ym" short display (e.g., "3h 42m")
 * Used for marathon target times
 */
export function formatDurationShort(seconds: number): string {
  if (!seconds || seconds <= 0) return "0h 0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

/**
 * Marathon distance constant
 */
export const MARATHON_DISTANCE_KM = 42.195;

/**
 * Format marathon participant progress for leaderboard display
 * Shows finish time if completed, otherwise shows distance
 */
export function formatMarathonProgress(
  totalDistance: number,
  totalDuration: number,
  targetDistance: number = MARATHON_DISTANCE_KM
): string {
  const isFinished = totalDistance >= targetDistance;
  if (isFinished && totalDuration > 0) {
    return `${formatDurationClock(totalDuration)} ✓`;
  }
  return `${totalDistance.toFixed(1)} km`;
}

/**
 * Check if a marathon participant is a winner
 * Must complete full distance within target time
 */
export function isMarathonWinner(
  totalDistance: number,
  totalDuration: number,
  targetDurationSeconds: number | null,
  targetDistance: number = MARATHON_DISTANCE_KM
): boolean {
  const completedDistance = totalDistance >= targetDistance;
  const withinTimeLimit = targetDurationSeconds
    ? totalDuration <= targetDurationSeconds
    : true;
  return completedDistance && withinTimeLimit;
}
